/*
  Publishes --rf-bottom-inset: how many pixels at the bottom of the viewport are
  already spoken for by something fixed that we do not own.

  Shopify's cookie consent banner sits at z-index 2,000,000 and can cover close
  to half a phone screen. That banner is a legal requirement and belongs on top,
  so nothing in this theme tries to out-stack it. Bottom-anchored theme UI reads
  this value and steps out of the way instead. When the banner is dismissed the
  value returns to 0 and everything drops back to the bottom edge.

  See CLAUDE.md §9.1.
*/

(function () {
  const OCCUPANTS = [
    // Shopify's customer privacy / cookie consent banner.
    //
    // The class here was '.shopify-pc__banner', which matches NOTHING on the
    // live store — measured: 0 elements. The fixed, z-index 2000000 root is
    // section.shopify-pc__banner__dialog, and '.shopify-pc__banner' is only a
    // prefix of that name, not a class Shopify actually sets. So this whole
    // mechanism has been publishing 0px since it was written, and every piece
    // of bottom-anchored UI that reads it — the PDP sticky bar, the cart
    // drawer, the header menu drawer, the filter drawer's Apply footer — has
    // been sitting under a 393px banner rather than above it.
    //
    // It failed silently, which is the point worth remembering: nothing threw,
    // the variable was always a valid '0px', and the CSS all looked right.
    //
    // Matched by prefix now, so a renamed suffix does not kill it again.
    // Children of the dialog match this too, but they are position:static and
    // isOnScreen() drops them, so only the fixed root is ever counted.
    '[class^="shopify-pc__banner"]',
    // The theme preview bar. Only present while previewing an unpublished
    // theme, so it measures 0 for real customers — but it keeps the bar
    // reachable while the owner is testing.
    '#PBarNextFrameWrapper',
  ];

  const root = document.documentElement;
  let lastValue = -1;
  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
  const watched = new WeakSet();
  let frame = null;

  function isOnScreen(el) {
    const cs = getComputedStyle(el);
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return false;
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    if (parseFloat(cs.opacity) === 0) return false;
    return true;
  }

  // Deliberately window.innerHeight — the LARGE viewport, the height the page
  // would have with the address bar hidden — and NOT clientHeight, which is
  // what is actually on screen. The two differ by the height of the address
  // bar, measured at 48px in testing.
  //
  // The value published here is consumed as `bottom:` on position:fixed
  // elements (the cart drawer, the header drawer, the PDP sticky bar), and a
  // fixed element's `bottom` is resolved against the large viewport. Verified
  // with a probe: with the variable at 345px, a fixed span landed at y=515,
  // matching innerHeight − 345 and not clientHeight − 345.
  //
  // So this must stay in the large-viewport frame or every one of those three
  // slides 48px down into the banner. Anything that needs the visible frame
  // instead should clamp itself with dvh, as .mobile-facets__inner does in
  // rf-dawn-retoken.css.
  function viewportHeight() {
    return window.innerHeight;
  }

  function measure() {
    frame = null;
    let inset = 0;

    OCCUPANTS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (resizeObserver && !watched.has(el)) {
          resizeObserver.observe(el);
          watched.add(el);
        }
        if (!isOnScreen(el)) return;

        const rect = el.getBoundingClientRect();
        if (rect.height === 0) return;
        // Only count it if it is actually anchored to the bottom edge.
        if (rect.bottom < viewportHeight() - 2) return;

        const covered = viewportHeight() - Math.max(rect.top, 0);
        if (covered > inset) inset = covered;
      });
    });

    inset = Math.round(inset);
    if (inset === lastValue) return;
    lastValue = inset;
    root.style.setProperty('--rf-bottom-inset', inset + 'px');
    document.dispatchEvent(new CustomEvent('rf:bottom-inset', { detail: { inset } }));
  }

  // Found live, on a real page load: requestAnimationFrame does not fire
  // for a hidden/backgrounded tab, and with only `if (frame) return` guarding
  // re-entry, one rAF that never comes locks schedule() out permanently —
  // every later call (resize, the observers, the poll below) turns into a
  // no-op forever, not just a delay. --rf-bottom-inset was confirmed stuck
  // at its very first '0px' for 8+ seconds on a page where the tab's own
  // visibilityState read 'hidden', while the exact same measurement run by
  // hand read the correct 394px throughout — the logic was never wrong, the
  // debounce just never got to run it again. A setTimeout alongside the
  // rAF guarantees a way out: whichever fires first wins and clears the
  // guard, so a hidden tab still gets measured, just up to 200ms slower
  // instead of never.
  function schedule() {
    if (frame) return;
    frame = true;
    var ran = false;
    function run() {
      if (ran) return;
      ran = true;
      frame = null;
      measure();
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    setTimeout(run, 200);
  }

  measure();

  // The banner arrives on its own schedule, so re-measure once everything has
  // settled as well as reacting to the observers below.
  window.addEventListener('load', schedule);

  // The banner is injected asynchronously, so watch for it arriving or leaving.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  document.addEventListener('visibilitychange', schedule);
  // Harmless if it never fires; kept in case some future occupant's entrance
  // is a plain CSS transition the two triggers above would otherwise miss.
  document.addEventListener('transitionend', schedule, true);

  // Measured live and confirmed wrong: none of the triggers above actually
  // catch the consent banner arriving. A MutationObserver attached at
  // document start, watching document.body the whole time, logged zero
  // childList insertions and zero transitionend events for it — the banner
  // was already present by the time any of this theme's own script had a
  // chance to observe anything, on a page load where --rf-bottom-inset
  // still measured 0px afterwards. Whatever inserts it — a document.write,
  // something ahead of this script, a shadow-root-adjacent trick — happens
  // somewhere this script cannot see coming, so waiting for a signal is the
  // wrong shape for this problem. Polling for a few seconds after load
  // costs nothing (measure() is a handful of querySelectorAll calls) and
  // does not care which mechanism the banner used to appear.
  var pollUntil = Date.now() + 8000;
  var pollTimer = setInterval(function () {
    schedule();
    if (Date.now() > pollUntil) clearInterval(pollTimer);
  }, 300);
})();
